import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useEntitlementStore } from '@caradasstore/EntitlementStore'
import { useUserStore } from '@caradasstore/UserStore'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { requiredTierFor, tierById } from '../../data/catalog'
import { useCatalog } from '../../store/CatalogStore'
import { Card, DarkButton, Eyebrow, OutlineButton } from './BillingUi'

const BILLING_PATH = '/settings?section=billing'

/**
 * What PrivateRoute renders in place of a page whose module the active center's plan does not
 * include (or when the center has no subscription at all). Explains, and sends owners and
 * managers to Billing; techs are told who to ask.
 */
export const UpgradeRequired = ({ module }) => {
  const history = useHistory()
  const user = useUserStore((store) => store.currentUser)
  const { hasSubscription, tier, trialPaused, state, clientPortal } = useEntitlementStore()
  const { catalog } = useCatalog()

  const canManage = Boolean(user && (user.is_owner || user.is_manager || user.is_admin))
  const needsSubscription = hasSubscription === false
  const paymentFailed = state === 'PAST_DUE'
  const neededTier = requiredTierFor(catalog, module)?.name ?? 'a higher plan'
  const currentTier = tierById(catalog, tier)?.name

  // A body-shop user on a plan without the client portal (0173): nothing is available to them.
  const noPortal = Boolean(user?.is_client_user) && clientPortal === false
  const title = noPortal
    ? 'Body-shop sign-in isn’t included'
    : trialPaused
      ? 'Trials are paused'
      : paymentFailed
        ? 'This location is paused'
        : needsSubscription
          ? 'This location has no active subscription'
          : `Included in ${neededTier}`
  let body
  if (noPortal) {
    body = `The calibration center's plan doesn't include the client portal${currentTier ? ` (${currentTier})` : ''}. Please contact the calibration center.`
  } else if (trialPaused) {
    body = canManage
      ? 'Start your plan in Settings → Billing to unlock this section again. Your data is kept.'
      : 'Ask your account owner to subscribe in Settings → Billing.'
  } else if (paymentFailed) {
    body = canManage
      ? 'A payment failed. Update the payment method in Settings → Billing to unlock everything again.'
      : 'A payment failed. Ask your account owner to update the payment method in Settings → Billing.'
  } else if (needsSubscription) {
    body = canManage
      ? 'Choose a plan to unlock this section and the rest of the platform.'
      : 'Ask your account owner to choose a plan in Settings → Billing.'
  } else {
    body = canManage
      ? `${currentTier ? `Your ${currentTier} plan` : 'Your current plan'} doesn’t include this section. Upgrade to ${neededTier} in Settings → Billing.`
      : `Your location’s plan doesn’t include this section. Ask your account owner about ${neededTier}.`
  }

  return (
    <AdasBox className='mx-auto mt-10 max-w-[560px]'>
      <Card className='px-8 py-10 text-center'>
        <AdasBox className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted'>
          <Lock className='h-5 w-5 text-muted-foreground' aria-hidden='true' />
        </AdasBox>
        <Eyebrow className='mb-2'>
          {trialPaused ? 'Trial paused' : paymentFailed ? 'Payment failed' : needsSubscription ? 'Subscription required' : 'Plan upgrade'}
        </Eyebrow>
        <AdasTypography className='text-[20px] font-bold text-foreground'>{title}</AdasTypography>
        <AdasTypography className='mx-auto mt-2 max-w-[440px] text-[14px] text-muted-foreground'>{body}</AdasTypography>
        <AdasBox className='mt-6 flex flex-wrap justify-center gap-3'>
          {canManage && <DarkButton onClick={() => history.push(BILLING_PATH)}>Go to Billing</DarkButton>}
          <OutlineButton onClick={() => history.push('/')}>Back to Home</OutlineButton>
        </AdasBox>
      </Card>
    </AdasBox>
  )
}
UpgradeRequired.propTypes = {
  module: PropTypes.string
}
